import { useMemo, useRef, useState } from "react";

const formatNumber = (value, digits = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0";
  return num.toFixed(digits).replace(/\.?0+$/, "");
};

const capitalizeLabel = (value) =>
  String(value || "")
    .split(/[_-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const VisualSummaryPanel = ({ summary, compact = false }) => {
  const [expandedFrames, setExpandedFrames] = useState(() => new Set());
  const [copyState, setCopyState] = useState({ type: "", message: "" });
  const [summaryMode, setSummaryMode] = useState("short");
  const copyTimerRef = useRef(null);

  const priorityIssues = summary?.priorityIssues || [];
  const shortSummary = summary?.shortSummary || null;
  const activeSummary = summaryMode === "short" && shortSummary ? shortSummary : summary;
  const frames = activeSummary?.frames || [];
  const topFrames = frames.slice(0, 8);
  const issueCounts = summary?.globalDiagnostics?.issueCounts || {};
  const hasShortSummary = Boolean(shortSummary);
  const activeScores =
    summaryMode === "short" && shortSummary
      ? shortSummary.overview || {}
      : summary?.globalDiagnostics || {};

  const dominantProblems = useMemo(
    () => summary?.globalDiagnostics?.dominantProblems || [],
    [summary],
  );

  const handleCopy = async (value, successMessage) => {
    const text = typeof value === "string" ? value : JSON.stringify(value, null, 2);
    try {
      await navigator.clipboard?.writeText(text);
      setCopyState({ type: "success", message: successMessage });
    } catch {
      setCopyState({ type: "error", message: "Copy failed" });
    }
    window.clearTimeout(copyTimerRef.current);
    copyTimerRef.current = window.setTimeout(() => {
      setCopyState({ type: "", message: "" });
    }, 1800);
  };

  const toggleFrame = (time) => {
    setExpandedFrames((prev) => {
      const next = new Set(prev);
      if (next.has(time)) next.delete(time);
      else next.add(time);
      return next;
    });
  };

  return (
    <section
      className={`animation-workbench-visual-summary${compact ? " compact" : ""}`}
    >
      <div className="animation-workbench-panel-head">
        <h3>Visual Summary</h3>
        <div className="animation-workbench-panel-actions animation-workbench-panel-actions-wrap">
          {hasShortSummary ? (
            <div className="animation-workbench-segmented-control" role="tablist" aria-label="Summary mode">
              <button
                type="button"
                className={`animation-workbench-segmented-btn${summaryMode === "full" ? " active" : ""}`}
                onClick={() => setSummaryMode("full")}
              >
                Full
              </button>
              <button
                type="button"
                className={`animation-workbench-segmented-btn${summaryMode === "short" ? " active" : ""}`}
                onClick={() => setSummaryMode("short")}
              >
                Short
              </button>
            </div>
          ) : null}
          <button
            type="button"
            className="animation-workbench-btn"
            onClick={() => handleCopy(activeSummary || {}, `${capitalizeLabel(summaryMode)} summary copied`)}
            disabled={!summary}
          >
            Copy JSON
          </button>
          <button
            type="button"
            className="animation-workbench-btn"
            onClick={() => handleCopy(priorityIssues, "Priority issues copied")}
            disabled={!priorityIssues.length}
          >
            Copy Issues
          </button>
        </div>
      </div>
      {copyState.message ? (
        <p className={`animation-workbench-copy-status ${copyState.type}`}>
          {copyState.message}
        </p>
      ) : null}

      {!summary ? (
        <div className="animation-workbench-visual-empty">
          <p>Run Preview to generate an AI-ready visual summary.</p>
          <span>
            The panel will show sampled frames, dominant issues, and suggested fixes.
          </span>
        </div>
      ) : (
        <div className="animation-workbench-visual-body">
          <div className="animation-workbench-visual-stats">
            <article className="animation-workbench-visual-stat">
              <span>Samples</span>
              <strong>{summary.sampling?.totalSamples || frames.length || 0}</strong>
            </article>
            <article className="animation-workbench-visual-stat">
              <span>Readability</span>
              <strong>{formatNumber(activeScores.overallReadabilityScore || activeScores.readabilityScore || 0)}</strong>
            </article>
            <article className="animation-workbench-visual-stat">
              <span>Composition</span>
              <strong>{formatNumber(activeScores.overallCompositionScore || activeScores.compositionScore || 0)}</strong>
            </article>
            <article className="animation-workbench-visual-stat">
              <span>Motion</span>
              <strong>{formatNumber(activeScores.overallMotionClarityScore || activeScores.motionClarityScore || 0)}</strong>
            </article>
          </div>

          <div className="animation-workbench-visual-section">
            <div className="animation-workbench-visual-section-title">
              Dominant Problems
            </div>
            {dominantProblems.length ? (
              <div className="animation-workbench-chip-list">
                {dominantProblems.map((problem) => (
                  <span key={problem} className="animation-workbench-chip">
                    {problem}
                  </span>
                ))}
              </div>
            ) : (
              <p className="animation-workbench-visual-muted">
                No major problems detected in sampled frames.
              </p>
            )}
          </div>

          <div className="animation-workbench-visual-section">
            <div className="animation-workbench-visual-section-title">Issue Counts</div>
            <div className="animation-workbench-chip-list">
              {Object.entries(issueCounts).map(([key, value]) => (
                <span key={key} className="animation-workbench-chip subdued">
                  {key}: {value}
                </span>
              ))}
            </div>
          </div>

          <div className="animation-workbench-visual-section">
            <div className="animation-workbench-visual-section-title">
              Priority Issues
            </div>
            {priorityIssues.length ? (
              <div className="animation-workbench-visual-list">
                {priorityIssues.map((issue) => (
                  <article key={issue.issueId} className="animation-workbench-visual-card">
                    <div className="animation-workbench-visual-row">
                      <strong>P{issue.priority}</strong>
                      <span className="animation-workbench-visual-muted">{issue.issueId}</span>
                    </div>
                    <p>{issue.reason}</p>
                  </article>
                ))}
              </div>
            ) : (
              <p className="animation-workbench-visual-muted">
                No priority issues in the current preview.
              </p>
            )}
          </div>

          <div className="animation-workbench-visual-section">
            <div className="animation-workbench-visual-section-title">
              Sampled Frames
            </div>
            <div className="animation-workbench-visual-list">
              {(compact ? topFrames.slice(0, 6) : topFrames).map((frame) => {
                const expanded = expandedFrames.has(frame.time);
                const frameFocus = frame.scene?.focusCandidate || frame.focus || "no focus";
                const frameIssueCount =
                  summaryMode === "short"
                    ? frame.components?.reduce(
                        (count, component) => count + (component.issues?.length || 0),
                        0,
                      ) || 0
                    : frame.issues?.length || 0;
                return (
                  <article key={frame.time} className="animation-workbench-visual-card">
                    <button
                      type="button"
                      className="animation-workbench-visual-frame-toggle"
                      onClick={() => toggleFrame(frame.time)}
                    >
                      <span>{formatNumber(frame.time, 1)}s</span>
                      <span>
                        {frameFocus} | issues {frameIssueCount}
                      </span>
                    </button>
                    <p>{frame.scene?.summary || frame.scene || "No scene summary available."}</p>
                    <div className="animation-workbench-chip-list">
                      <span className="animation-workbench-chip subdued">
                        visible {frame.scene?.visibleCount || frame.components?.length || 0}
                      </span>
                      <span className="animation-workbench-chip subdued">
                        clutter {formatNumber(frame.scene?.clutterScore || 0)}
                      </span>
                      {summaryMode === "short" ? (
                        <span className="animation-workbench-chip subdued">
                          connectors {frame.connectors?.length || 0}
                        </span>
                      ) : (
                        <span className="animation-workbench-chip subdued">
                          overflow {frame.scene?.textOverflowCount || 0}
                        </span>
                      )}
                    </div>
                    {expanded && (
                      <div className="animation-workbench-visual-frame-details">
                        {summaryMode === "short" ? (
                          <>
                            {(frame.changes || []).length ? (
                              <div className="animation-workbench-visual-subsection">
                                <h4>Changes</h4>
                                {(frame.changes || []).map((change) => (
                                  <div key={change} className="animation-workbench-visual-detail-row">
                                    <span>{change}</span>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                            {(frame.components || []).length ? (
                              <div className="animation-workbench-visual-subsection">
                                <h4>Components</h4>
                                {(frame.components || []).map((component) => (
                                  <div
                                    key={`${frame.time}-${component.name}`}
                                    className="animation-workbench-visual-detail-row"
                                  >
                                    <strong>
                                      {component.name} · {component.position} · {component.size}
                                    </strong>
                                    <span>
                                      {[
                                        component.role ? capitalizeLabel(component.role) : null,
                                        component.shape ? `shape ${component.shape}` : null,
                                        component.text ? `text "${component.text}"` : null,
                                        component.issues?.length
                                          ? `issues: ${component.issues.join(", ")}`
                                          : null,
                                      ]
                                        .filter(Boolean)
                                        .join(" | ")}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <>
                            {(frame.issues || []).length ? (
                              <div className="animation-workbench-visual-subsection">
                                <h4>Issues</h4>
                                {(frame.issues || []).map((issue) => (
                                  <div key={issue.id} className="animation-workbench-visual-detail-row">
                                    <strong>{issue.type}</strong>
                                    <span>{issue.message}</span>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                            {(frame.recommendedActions || []).length ? (
                              <div className="animation-workbench-visual-subsection">
                                <h4>Suggested Fixes</h4>
                                {(frame.recommendedActions || []).map((action) => (
                                  <div key={action.id} className="animation-workbench-visual-detail-row">
                                    <strong>{action.kind}</strong>
                                    <span>{action.message}</span>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </div>

          <details className="animation-workbench-visual-raw">
            <summary>Raw JSON</summary>
            <pre>{JSON.stringify(activeSummary, null, 2)}</pre>
          </details>
        </div>
      )}
    </section>
  );
};

export default VisualSummaryPanel;
